"use client";

import { useCallback } from "react";
import EverflowSDK from "@everflow/everflow-sdk";
import { captureException } from "@sentry/nextjs";
import cookies from "js-cookie";
import { EVERFLOW_OFFER_ID } from "@portal/app/_common/types/everflow";
import { COOKIE_KEYS } from "@portal/app/_common/lib/cookieKeys";
import {
  parseEverflowAffiliateParamsCookieValue,
  parseEverflowAffiliateParamsFromSearch,
} from "@portal/app/_common/lib/everflowAffiliateParams";
import {
  buildFlowStepEvent,
  type FlowStepDescriptor,
  type FlowStepInput,
} from "@portal/app/_common/lib/flowStepTracking";

function readUid(): string | undefined {
  try {
    const landing = parseEverflowAffiliateParamsFromSearch(window.location.search);
    // A new attributed landing replaces the previous landing's whole capture.
    if (landing) return landing.everflowOfferUrlId;
    return parseEverflowAffiliateParamsCookieValue(
      cookies.get(COOKIE_KEYS.EVERFLOW_AFFILIATE_PARAMS)
    )?.everflowOfferUrlId;
  } catch {
    return;
  }
}

function readTransactionId(): string | undefined {
  try {
    // Read at the action, not mount. Never initiate a second tracking handshake.
    return EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID) || undefined;
  } catch {
    return;
  }
}

/** Queue before navigation; analytics must never prevent a successful user action. */
export function trackFlowStep(input: Omit<FlowStepInput, "uid" | "transactionId">): void {
  if (typeof window === "undefined") return;
  try {
    const event = buildFlowStepEvent({
      ...input,
      uid: readUid(),
      transactionId: readTransactionId(),
    });
    if (!event) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  } catch {
    // Do not attach answers, cookies, or the underlying exception's message.
    try {
      captureException(new Error("Unable to queue flow_step analytics event"));
    } catch {
      // Error reporting must not affect progression either.
    }
  }
}

export function useFlowStepTracking(flowName: string, enabled = true) {
  return useCallback(
    (descriptor: FlowStepDescriptor) => {
      if (enabled) trackFlowStep({ flowName, ...descriptor });
    },
    [flowName, enabled]
  );
}
